/* Command Palette · Cmd+K / Ctrl+K
   Linear-style quick launcher: fuzzy-search across navigation routes
   AND high-frequency actions (Add client / case / contractor / report).
   Renders inline modals so the user never leaves their current page. */
import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, ArrowRight, Plus, Buildings, FolderOpen,
         Briefcase, UserCircle, Cube, X as XIcon, House, Receipt,
         CurrencyInr, Warning, Star, MapTrifold, Camera, Eye,
         Globe, ShieldCheck, Stamp, ChartBar, ChartLine, CreditCard,
         Code, MapPinLine, Pulse, WhatsappLogo, Lightning, Gear,
         Command as CmdIcon } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const NAV_COMMANDS = [
  { id: "go-dashboard",    label: "Go to Dashboard",      to: "/dashboard",     Icon: House,         keywords: ["home", "kpi"] },
  { id: "go-cases",        label: "Go to Cases",          to: "/cases",         Icon: FolderOpen,    keywords: ["case", "investigation"] },
  { id: "go-clients",      label: "Go to Clients",        to: "/clients",       Icon: Buildings,     keywords: ["brand", "customer"] },
  { id: "go-contractors",  label: "Go to Suppliers",      to: "/contractors",   Icon: Briefcase,     keywords: ["vendor", "kyc", "supplier", "contractor"] },
  { id: "go-products",     label: "Go to Products",       to: "/products",      Icon: Cube,          keywords: ["sku", "library"] },
  { id: "go-parties",      label: "Go to Parties",        to: "/parties",       Icon: UserCircle,    keywords: ["seller", "counterfeiter"] },
  { id: "go-invoices",     label: "Go to Invoices",       to: "/invoices",      Icon: Receipt,       keywords: ["bill"] },
  { id: "go-payables",     label: "Go to Payables",       to: "/payables",      Icon: CurrencyInr,   keywords: ["pay"] },
  { id: "go-escalations",  label: "Go to Escalations",    to: "/escalations",   Icon: Warning,       keywords: ["ceo queue"] },
  { id: "go-leads",        label: "Go to Leads",          to: "/leads",         Icon: Star,          keywords: ["pipeline"] },
  { id: "go-fieldops",     label: "Go to Field Ops",      to: "/field-ops",     Icon: MapTrifold,    keywords: ["officer"] },
  { id: "go-capture",      label: "Go to Capture",        to: "/capture",       Icon: Camera,        keywords: ["photo", "offline", "pwa"] },
  { id: "go-whatsapp",     label: "Go to WhatsApp inbox", to: "/whatsapp",      Icon: WhatsappLogo,  keywords: ["wa", "twilio"] },
  { id: "go-map",          label: "Go to Map",            to: "/map",           Icon: MapPinLine,    keywords: ["gis", "heatmap"] },
  { id: "go-intel",        label: "Go to Intelligence",   to: "/intelligence",  Icon: Eye,           keywords: ["osint"] },
  { id: "go-network",      label: "Go to Network",        to: "/network",       Icon: Globe,         keywords: ["graph", "counsel"] },
  { id: "go-quality",      label: "Go to Quality",        to: "/quality",       Icon: ShieldCheck,   keywords: ["qa"] },
  { id: "go-gov-verify",   label: "Go to Gov Verify",     to: "/gov-verify",    Icon: Stamp,         keywords: ["fda", "dgda", "cdsco", "license"] },
  { id: "go-analytics",    label: "Go to Analytics",      to: "/analytics",     Icon: ChartLine,     keywords: ["chart"] },
  { id: "go-reports",      label: "Go to Reports",        to: "/reports",       Icon: ChartBar,      keywords: ["adhoc", "export"] },
  { id: "go-billing",      label: "Go to Billing",        to: "/billing",       Icon: CreditCard,    keywords: ["stripe", "plan"] },
  { id: "go-developer",    label: "Go to Developer",      to: "/developer",     Icon: Code,          keywords: ["api", "keys"] },
  { id: "go-webhooks",     label: "Go to Webhooks",       to: "/webhooks",      Icon: Lightning,     keywords: ["delivery", "retry"] },
  { id: "go-audit",        label: "Go to Audit log",      to: "/audit",         Icon: ShieldCheck,   keywords: ["log"] },
  { id: "go-build-status", label: "Go to Build Status",   to: "/admin/build-status", Icon: Pulse,    keywords: ["ceo", "gaps", "oversight"] },
  { id: "go-tap2verify",   label: "Go to Tap2Verify",     to: "/admin/tap2verify",   Icon: ShieldCheck, keywords: ["crowdsource", "curated", "verify"] },
  { id: "go-portal",       label: "Go to Client portal",  to: "/portal/cases",       Icon: Buildings, keywords: ["external", "client", "view"] },
  { id: "go-settings",     label: "Go to Settings",       to: "/settings",      Icon: Gear,          keywords: ["users", "gdpr", "integrations"] },
];

const ACTION_COMMANDS = [
  { id: "new-case",       label: "New case",            verb: "Create", section: "Create", Icon: FolderOpen, kind: "form_case",       keywords: ["add", "open"] },
  { id: "new-client",     label: "New client",          verb: "Create", section: "Create", Icon: Buildings,  kind: "form_client",     keywords: ["add", "brand"] },
  { id: "new-contractor", label: "New supplier",         verb: "Create", section: "Create", Icon: Briefcase,  kind: "form_contractor", keywords: ["add", "vendor", "contractor", "supplier"] },
  { id: "new-party",      label: "New party",           verb: "Create", section: "Create", Icon: UserCircle, kind: "form_party",      keywords: ["add", "infringer"] },
  { id: "new-product",    label: "New product",         verb: "Create", section: "Create", Icon: Cube,       kind: "form_product",    keywords: ["add", "sku"] },
];

export default function CommandPalette() {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState("");
  const [hovered, setHovered] = useState(0);
  const [mode, setMode] = useState("list");   // list | form_client | form_case | …
  const inputRef = useRef(null);
  const nav = useNavigate();

  // Global Cmd+K listener
  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
        setQ(""); setMode("list");
      } else if (e.key === "Escape" && open) {
        setOpen(false);
      }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => { if (open) setTimeout(() => inputRef.current?.focus(), 50); }, [open, mode]);

  const all = useMemo(() => [...ACTION_COMMANDS, ...NAV_COMMANDS], []);
  const matches = useMemo(() => {
    if (!q.trim()) return all;
    const t = q.trim().toLowerCase();
    return all
      .map((c) => {
        const hay = `${c.label} ${(c.keywords || []).join(" ")}`.toLowerCase();
        const idx = hay.indexOf(t);
        return idx >= 0 ? { c, score: idx } : null;
      })
      .filter(Boolean)
      .sort((a, b) => a.score - b.score)
      .map((x) => x.c);
  }, [q, all]);

  useEffect(() => { setHovered(0); }, [q]);

  const select = (cmd) => {
    if (cmd.to) {
      nav(cmd.to);
      setOpen(false);
    } else if (cmd.kind?.startsWith("form_")) {
      setMode(cmd.kind);
      setQ("");
    }
  };

  const onArrowKey = (e) => {
    if (mode !== "list") return;
    if (e.key === "ArrowDown") {
      e.preventDefault(); setHovered((h) => Math.min(matches.length - 1, h + 1));
    } else if (e.key === "ArrowUp") {
      e.preventDefault(); setHovered((h) => Math.max(0, h - 1));
    } else if (e.key === "Enter" && matches[hovered]) {
      e.preventDefault(); select(matches[hovered]);
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[100] bg-black/40 backdrop-blur-sm flex items-start justify-center pt-[15vh]"
         onClick={() => setOpen(false)} data-testid="command-palette">
      <div className="bg-[var(--span-surface)] border border-[var(--span-border-strong)] w-full max-w-xl shadow-2xl"
           onClick={(e) => e.stopPropagation()}>
        {mode === "list" && (
          <>
            <div className="flex items-center px-4 py-3 border-b border-[var(--span-border)]">
              <MagnifyingGlass size={16} className="text-[var(--span-text-2)]"/>
              <input ref={inputRef} value={q} onChange={(e) => setQ(e.target.value)}
                     onKeyDown={onArrowKey} placeholder="Type to search… (jump anywhere, create anything)"
                     className="bg-transparent border-0 outline-none w-full px-3 text-[14px]"
                     data-testid="cmdk-input"/>
              <span className="text-[10px] font-mono uppercase tracking-wider text-[var(--span-text-2)] hairline px-2 py-0.5">
                ESC
              </span>
            </div>
            <ul className="max-h-[55vh] overflow-y-auto py-1" data-testid="cmdk-results">
              {matches.length === 0 && (
                <li className="px-4 py-6 text-center text-[12px] text-[var(--span-text-2)]">No match. Try "new case", "map", "settings"…</li>
              )}
              {matches.map((c, idx) => {
                const Icon = c.Icon;
                return (
                  <li key={c.id}
                      data-testid={`cmdk-item-${c.id}`}
                      onMouseEnter={() => setHovered(idx)}
                      onClick={() => select(c)}
                      className={`flex items-center gap-3 px-4 py-2.5 cursor-pointer ${idx === hovered ? "bg-[var(--span-surface-2)]" : ""}`}>
                    <Icon size={14} className="text-[var(--span-text-2)]"/>
                    <div className="flex-1 text-[13px]">
                      {c.section && <span className="text-[10px] font-mono uppercase mr-2 text-[var(--span-text-2)]">{c.section} ›</span>}
                      {c.label}
                    </div>
                    <ArrowRight size={11} className="text-[var(--span-text-2)]"/>
                  </li>
                );
              })}
            </ul>
            <div className="border-t border-[var(--span-border)] px-4 py-2 flex items-center justify-between text-[10px] font-mono uppercase tracking-wider text-[var(--span-text-2)]">
              <span className="flex items-center gap-1"><CmdIcon size={11}/> K · TOGGLE</span>
              <span>↑↓ NAVIGATE · ↵ SELECT</span>
            </div>
          </>
        )}

        {mode === "form_client" && <QuickClientForm onDone={() => setOpen(false)} onBack={() => setMode("list")}/>}
        {mode === "form_case" && <QuickCaseForm onDone={() => setOpen(false)} onBack={() => setMode("list")}/>}
        {mode === "form_contractor" && <QuickContractorForm onDone={() => setOpen(false)} onBack={() => setMode("list")}/>}
        {mode === "form_party" && <QuickPartyForm onDone={() => setOpen(false)} onBack={() => setMode("list")}/>}
        {mode === "form_product" && <QuickProductForm onDone={() => setOpen(false)} onBack={() => setMode("list")}/>}
      </div>
    </div>
  );
}

// ----------------------------------------------------------------------------
// Quick forms (minimal happy-path; full forms still live on the dedicated pages)
// ----------------------------------------------------------------------------

const COUNTRIES = ["IN", "BD", "PK", "SG", "AE", "EG", "CH", "UK", "US", "NP", "TH"];

function FormFrame({ title, busy, onBack, onSubmit, error, children, testId }) {
  return (
    <form onSubmit={onSubmit} className="p-5 space-y-3" data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="label-eyebrow">{title}</div>
        <button type="button" onClick={onBack} className="text-[10px] font-mono text-[var(--span-text-2)] uppercase">← BACK</button>
      </div>
      {children}
      {error && <div className="text-[11px] text-[var(--span-danger)] font-mono">{String(error)}</div>}
      <div className="flex justify-end gap-2 pt-1">
        <button disabled={busy} className="span-btn" data-testid="cmdk-submit"><Plus size={12}/> Save</button>
      </div>
    </form>
  );
}

function Field({ label, ...rest }) {
  return (
    <div>
      <label className="label-eyebrow block mb-1">{label}</label>
      <input className="span-input" {...rest}/>
    </div>
  );
}

function QuickClientForm({ onDone, onBack }) {
  const [name, setName] = useState("");
  const [code, setCode] = useState("");
  const [country, setCountry] = useState("IN");
  const [industry, setIndustry] = useState("Mixed");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post("/clients", { name, code: (code || name.split(/\s+/).map(w => w[0]).join("").toUpperCase()).slice(0, 8), country_code: country, industry });
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <FormFrame title="New client" busy={busy} onBack={onBack} onSubmit={submit} error={err} testId="cmdk-client-form">
      <Field label="Brand / Company name *" autoFocus required value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Bajaj Auto Ltd"/>
      <div className="grid grid-cols-3 gap-3">
        <Field label="Code" value={code} onChange={(e) => setCode(e.target.value.toUpperCase())} placeholder="auto"/>
        <div>
          <label className="label-eyebrow block mb-1">Country</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="span-input">
            {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <Field label="Industry" value={industry} onChange={(e) => setIndustry(e.target.value)}/>
      </div>
    </FormFrame>
  );
}

function QuickCaseForm({ onDone, onBack }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ title: "", code: "INV", country: "IN", client_id: "", priority: "medium" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data || [])); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_id) { setErr("Pick a client"); return; }
    setBusy(true); setErr(null);
    try {
      await api.post("/cases", form);
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <FormFrame title="New case" busy={busy} onBack={onBack} onSubmit={submit} error={err} testId="cmdk-case-form">
      <Field label="Case title *" autoFocus required value={form.title} onChange={(e) => set("title", e.target.value)} placeholder="e.g. Counterfeit spare parts — Delhi NCR"/>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Code</label>
          <select value={form.code} onChange={(e) => set("code", e.target.value)} className="span-input">
            {["INV", "CON", "LIT", "INF", "GEN", "ADM", "INT", "FIR", "CoC", "FIN", "FoR", "BD"].map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Country</label>
          <select value={form.country} onChange={(e) => set("country", e.target.value)} className="span-input">
            {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Client *</label>
        <select value={form.client_id} onChange={(e) => set("client_id", e.target.value)} className="span-input" required>
          <option value="">— Pick a client —</option>
          {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name} ({c.code})</option>)}
        </select>
      </div>
    </FormFrame>
  );
}

function QuickContractorForm({ onDone, onBack }) {
  const [name, setName] = useState("");
  const [country, setCountry] = useState("IN");
  const [type, setType] = useState("Individual");
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true); setErr(null);
    try {
      await api.post("/contractors", { full_name: name, country_code: country, contractor_type: type });
      onDone();
    } catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <FormFrame title="New supplier" busy={busy} onBack={onBack} onSubmit={submit} error={err} testId="cmdk-contractor-form">
      <Field label="Full name *" autoFocus required value={name} onChange={(e) => setName(e.target.value)}/>
      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Type</label>
          <select value={type} onChange={(e) => setType(e.target.value)} className="span-input">
            {["Individual", "Agency", "Vendor", "Lab", "Subcontractor"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Country</label>
          <select value={country} onChange={(e) => setCountry(e.target.value)} className="span-input">
            {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
      </div>
      <div className="text-[10px] font-mono text-[var(--span-text-2)]">KYC + bank details: open the full /contractors page after save.</div>
    </FormFrame>
  );
}

function QuickPartyForm({ onDone, onBack }) {
  const [form, setForm] = useState({ name: "", type: "seller", country_code: "IN", city: "" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await api.post("/parties", form); onDone(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <FormFrame title="New party" busy={busy} onBack={onBack} onSubmit={submit} error={err} testId="cmdk-party-form">
      <Field label="Name *" autoFocus required value={form.name} onChange={(e) => set("name", e.target.value)}/>
      <div className="grid grid-cols-3 gap-3">
        <div>
          <label className="label-eyebrow block mb-1">Type</label>
          <select value={form.type} onChange={(e) => set("type", e.target.value)} className="span-input">
            {["seller", "distributor", "manufacturer", "wholesaler", "dark_web", "broker", "courier", "other"].map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="label-eyebrow block mb-1">Country</label>
          <select value={form.country_code} onChange={(e) => set("country_code", e.target.value)} className="span-input">
            {COUNTRIES.map((c) => <option key={c}>{c}</option>)}
          </select>
        </div>
        <Field label="City" value={form.city} onChange={(e) => set("city", e.target.value)}/>
      </div>
    </FormFrame>
  );
}

function QuickProductForm({ onDone, onBack }) {
  const [clients, setClients] = useState([]);
  const [form, setForm] = useState({ name: "", sku: "", category: "", brand_id: "" });
  const [err, setErr] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/clients").then(({ data }) => setClients(data || [])); }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const submit = async (e) => {
    e.preventDefault(); setBusy(true); setErr(null);
    try { await api.post("/products", { ...form, auth_marks: [] }); onDone(); }
    catch (e) { setErr(e.response?.data?.detail || e.message); }
    finally { setBusy(false); }
  };
  return (
    <FormFrame title="New product" busy={busy} onBack={onBack} onSubmit={submit} error={err} testId="cmdk-product-form">
      <Field label="Name *" autoFocus required value={form.name} onChange={(e) => set("name", e.target.value)}/>
      <div className="grid grid-cols-2 gap-3">
        <Field label="SKU" value={form.sku} onChange={(e) => set("sku", e.target.value)}/>
        <Field label="Category" value={form.category} onChange={(e) => set("category", e.target.value)}/>
      </div>
      <div>
        <label className="label-eyebrow block mb-1">Brand (client)</label>
        <select value={form.brand_id} onChange={(e) => set("brand_id", e.target.value)} className="span-input">
          <option value="">— None —</option>
          {clients.map((c) => <option key={c.client_id} value={c.client_id}>{c.name}</option>)}
        </select>
      </div>
    </FormFrame>
  );
}
